import {Component, OnDestroy, OnInit} from '@angular/core';
import {Observable, of, Subscription} from "rxjs";
import {CaseDocketService} from "../../../services/case-docket.service";
import {AttorneyInfoResponse} from "../../../models/docket";
import {ActivatedRoute, Router} from "@angular/router";
import {ProtestInfo} from "../../../models/protest";
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors} from "@angular/forms";
import {SidebarService} from "../../../services/sidebar.service";
import {StateService} from "../../../services/state.service";
import {UtilService} from "../../../services/util.service";
import {UserRoleString} from "../../../models/user";
import {map} from "rxjs/operators";


@Component({
    selector: 'app-join-unjoin-cases',
    templateUrl: './join-unjoin-cases.component.html',
    styleUrl: './join-unjoin-cases.component.scss',
    standalone: false
})
export class JoinUnjoinCasesComponent implements OnInit, OnDestroy {

  private a_No!: string;
  docketSub!: Subscription;
  subs: Subscription[] = [];
  docketResponse!: AttorneyInfoResponse;
  protestInfo!: ProtestInfo;
  loading = true;
  busy = false;

  joinUnjoinForm!: FormGroup;
  controlNum = 0;


  constructor(private readonly activatedRoute: ActivatedRoute,
              private readonly caseDocketService: CaseDocketService,
              private readonly fb: FormBuilder,
              private readonly sidebarService: SidebarService,
              private readonly stateService: StateService,
              private readonly router: Router,
              private readonly utilService: UtilService) {
  }

  ngOnInit(): void {
    this.a_No = this.activatedRoute.snapshot.params['a_No'];
    this.joinUnjoinForm = this.fb.group({});
    this.addBNumControl();

    this.caseDocketService.loadDocket(this.a_No);
    this.docketSub = this.caseDocketService.docketSub$.subscribe((response: AttorneyInfoResponse|undefined) => {
      if (!response) return;
      this.processCaseDocket(response);
      this.sidebarService.setNavByUser(this.stateService.user.role_id, this.protestInfo);
    });
  }

  ngOnDestroy(): void {
    this.docketSub.unsubscribe();
    this.subs.forEach(sub => sub.unsubscribe());
  }

  addBNumControl() {
    const control = new FormControl(null, {
      asyncValidators: [this.utilService.debouncedValidator(500, this.bNumValidator.bind(this))]
    });

    this.joinUnjoinForm.addControl('bNumField' + this.controlNum, control);

    // trigger mat error display without blur
    this.subs.push(control.valueChanges.subscribe(() => {
      control.markAsTouched();
    }));

    this.controlNum++;
  }

  processCaseDocket(response: AttorneyInfoResponse): void {
    console.debug(response);

    this.docketResponse = response;
    this.protestInfo = this.docketResponse.protestInfo;

    this.loading = false;
  }


  bNumValidator(control: AbstractControl) : Observable<ValidationErrors | null> {
    const value = control.value;
    let errors : ValidationErrors | null = null;

    if (value?.length >= 6) {
      const bNum = value;
      const parentBNum = this.protestInfo.b_No

      if (parentBNum) {
        return this.caseDocketService.validateJoin(bNum, parentBNum).pipe(
          map(val => {
            const response = val.response;

            if (response === 'valid') {
              return null;
            } else {
              return { error: response };
            }
          })
        );
      } else {
        errors = { error: 'Current case missing B#' };
      }

    } else if (control.dirty && value?.length >= 1) {
      errors = {error: 'B# must be 6 characters'};
    }

    return of(errors);
  }

  onAdd() {
    this.addBNumControl();
  }

  onRemove(controlName: string) {
    this.joinUnjoinForm.removeControl(controlName);
  }

  onJoin() {
    if (this.joinUnjoinForm.valid) {
      this.busy = true;
      const listOfBNumbers = Object.values(this.joinUnjoinForm.controls).map(control => control.value).join(',');

      this.caseDocketService.joinCases(this.protestInfo.a_No, listOfBNumbers, null, null).subscribe(response => {
        console.debug(response);

        if (response.isSuccess) {
          this.busy = false;
          this.router.navigate([UserRoleString[this.stateService.user.role_id] + '/case-docketsheet/' + this.a_No]);
          this.utilService.displayDialogMessage('Success', 'Successfully joined cases.').subscribe();
        } else {
          this.busy = false;
          this.utilService.displayDialogMessage('Error', 'There was a problem joining cases.');
        }
      });
    }
  }

  onUnJoin() {
    if (this.joinUnjoinForm.valid) {
      this.busy = true;
      const listOfBNumbers = Object.values(this.joinUnjoinForm.controls).map(control => control.value).join(',');

      this.caseDocketService.unJoinCases(this.protestInfo.a_No, listOfBNumbers, null, null).subscribe(response => {
        console.debug(response);

        if (response.isSuccess) {
          this.busy = false;
          this.router.navigate([UserRoleString[this.stateService.user.role_id] + '/case-docketsheet/' + this.a_No]);
          this.utilService.displayDialogMessage('Success', 'Successfully unjoined cases.').subscribe();
        } else {
          this.busy = false;
          this.utilService.displayDialogMessage('Error', 'There was a problem unjoining cases.');
        }
      });
    }
  }

  checkAllFieldsDirty(): boolean {
    return Object.values(this.joinUnjoinForm.controls)
      .filter(item =>  item.value?.length < 6 || !item.dirty)
      .length > 0;
  }


  protected readonly Object = Object;
}
